import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CalendarEvent, CalendarView, DAYS_OF_WEEK } from 'angular-calendar';
import * as moment from 'moment';
import { EventsService } from '../../services/events.service';
import { RoomService } from '../../services/room.service';
import { UserService } from '../../services/user.service';
import { CourseService } from '../../services/course.service';
import { MatRadioChange } from '@angular/material/radio';
import { Common } from '../../../shared/common';

interface InfoBox {
  bgClass: string;
  icon: string;
  title: string;
  subtitle: string;
}

moment.updateLocale('en', {
  week: {
    dow: DAYS_OF_WEEK.MONDAY,
    doy: 0,
  },
});

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss']
})
export class CalendarComponent implements OnInit {

  colNum: number = 4;
  rowHeight = '120px';
  cardClass = 'dash-card';
  isMobile = false;
  infoBoxes: InfoBox[];
  view: CalendarView = CalendarView.Day;
  viewDate: Date = new Date();
  events: CalendarEvent[] = [];
  faculties: any;
  rooms: any;
  courses: any;
  type: any = 'daily_view';
  view_type: any = 'all';
  view_type_value: any = '';
  date: any = Common.getMonday(new Date());
  displayedColumns = ["title"];
  activeDayIsOpen: boolean = true;
  courseDropdownSettings: {};
  roomDropdownSettings: {};
  facultyDropdownSettings: {};
  ShowFilter = true;
  dataSource: any = [];

  // Store the selected view (default to 'calendar')
  selectedView: string = 'calendar';

  // Method to set the selected view
  selectView(view: string): void {
    this.selectedView = view;
  }

  constructor(private eventService: EventsService,
    private changeDetectorRefs: ChangeDetectorRef,
    private roomService: RoomService,
    private courseService: CourseService,
    private facultyService: UserService) {
    this.date = Common.getDate(new Date());
    this.getWeeklyTimeTable(this.type);
  }

  download() {
    this.eventService.downloadData(this.date, this.type).subscribe((data: any) => {
      if (data.returnCode === 200) {
        Common.b64toBlob(data.result, "application/pdf", "report");
      }
    });
  }

  ngOnInit() {
    this.refresh();
    this.roomDropdownSettings = {
      singleSelection: true,
      idField: 'id',
      textField: 'room_no',
      selectAllText: 'Select All',
      closeDropDownOnSelection: true,
      unSelectAllText: 'UnSelect All',
      itemsShowLimit: 3,
      allowSearchFilter: this.ShowFilter
    };
    this.facultyDropdownSettings = {
      singleSelection: true,
      idField: 'id',
      textField: 'name',
      selectAllText: 'Select All',
      closeDropDownOnSelection: true,
      unSelectAllText: 'UnSelect All',
      itemsShowLimit: 3,
      allowSearchFilter: this.ShowFilter
    };
    this.courseDropdownSettings = {
      singleSelection: true,
      idField: 'id',
      textField: 'displayText',
      selectAllText: 'Select All',
      closeDropDownOnSelection: true,
      unSelectAllText: 'UnSelect All',
      itemsShowLimit: 3,
      allowSearchFilter: this.ShowFilter
    };
  }

  refresh() {
    this.changeDetectorRefs.detectChanges();
  }

  dayClicked({ date, events }: { date: Date; events: CalendarEvent[] }): void {
    if (moment(date).isSame(this.viewDate)) {
      if ((moment(this.viewDate).isSame(date) && this.activeDayIsOpen === true) ||
        events.length === 0
      ) {
        this.activeDayIsOpen = false;
      } else {
        this.activeDayIsOpen = true;
      }
      this.viewDate = date;
    } else {
      this.viewDate = date;
      this.activeDayIsOpen = true;
    }
  }

  onDateChange(event) {
    if (this.type == "monthly_view") {
      this.date = Common.getStartOfMonth(event);
    } else if (this.type == "weekly_view") {
      this.date = Common.getMonday(event);
    } else {
      this.date = Common.getDate(event);
      this.viewDate = this.date;
    }

    if (this.view_type == 'all') {
      this.getWeeklyTimeTable(this.type);
    } else {
      this.onTypeChange(this.view_type, this.view_type_value)
    }
  }

  onViewTypeChange(event) {
    let date = new Date();
    this.view_type = 'all';
    this.view_type_value = '';
    if (event == "month") {
      this.date = Common.getStartOfMonth(date);
      this.type = "monthly_view"
    } else if (event == "week") {
      this.type = "weekly_view"
      this.date = Common.getMonday(date);
    } else {
      this.type = "daily_view"
      this.date = Common.getDate(date);
      this.viewDate = date;
    }
    this.getWeeklyTimeTable(this.type);
  }

  typeChange(event: MatRadioChange) {
    console.log(event.value)
    this.view_type = event.value;
    if (this.view_type == 'room') {
      this.getAllRooms();
    } else if (this.view_type == 'course') {
      this.getAllCourses();
    } else if (this.view_type == 'faculty') {
      this.getAllFaculties();
    } else {
      this.getWeeklyTimeTable(this.type);
    }
  }

  getAllRooms() {
    this.roomService.getAllRooms().subscribe((data: any) => {
      this.rooms = data;
    });
  }

  getAllFaculties() {
    this.facultyService.getUsersByType("FACULTY").subscribe((data: any) => {
      this.faculties = data.result;
    });
  }

  getAllCourses() {
    this.courseService.getAllCourses().subscribe((data: any) => {
      this.courses = data;
      this.courses = this.courses.map(course => ({
        ...course,
        displayText: `${course.code} - ${course.name}`
      }));
    });
  }

  populateEvent(elements, view) {
    elements.forEach(element => {
      this.events = [...this.events,
      {
        start: new Date(element.startDate),
        end: new Date(element.endDate),
        title: element.title,
        color: { ...Common.colors[Common.getRandomColour(element.type)] },
        resizable: {
          beforeStart: true,
          afterEnd: true,
        },
        allDay: false,
        draggable: false,
      }
      ];
    });
  }

  getWeeklyTimeTable(type) {
    this.events = [];
    this.eventService.getSchedulesByType(this.date, type).subscribe((data: any) => {
      if (data.returnCode === 200) {
        this.dataSource = data.result;
        this.populateEvent(data.result, this.view);
        this.refresh();
      }
    });
  }

  onTypeChange(type, value) {
    this.events = [];
    this.eventService.getSchedulesByOther(type, value, this.date, this.type).subscribe((data: any) => {
      if (data.returnCode === 200) {
        this.dataSource = data.result;
        this.populateEvent(data.result, this.view);
        this.refresh();
      }
    });
  }

  onViewChange(event) {
    this.view_type_value = event.id;
    this.onTypeChange(this.view_type, this.view_type_value);
  }

}